import { useMemo } from "react";
import { fireblockCryptoWithdraw } from "../../../redux/actions/CryptoTransaction/Withdraw";
import { useDispatch } from "react-redux";
import WarningIcon from "../../icons/WarningIcon";
import { useExchangeRate } from "../../hooks/useExchangeRate";

// const NETWORK_FEE = 0.5;

const SummaryRow = ({
  label,
  value,
  isAddress = false,
  valueClassName = "text-sm font-medium text-text-primary",
}) => (
  <div className="flex w-full items-start justify-between gap-4">
    <p className="text-sm font-normal leading-[21px] text-text-secondary">{label}</p>
    {isAddress ? (
      <p className={`min-w-0 text-right ${valueClassName}`}>
        {value.slice(0, 6)}
        <span className="font-normal max-sm:hidden">{value.slice(6, -6)}</span>
        <span className="font-normal sm:hidden">...</span>
        {value.slice(-6)}
      </p>
    ) : (
      <p className={`min-w-0 text-right ${valueClassName}`}>{value}</p>
    )}
  </div>
);

const WithdrawConfirmOrderStep = ({
  selectedAsset,
  selectedNetwork,
  selectedBeneficiary,
  amount,
  submitting,
  setSubmitting,
  onConfirm,
  setCurrentStep,
  setError,
}) => {
  const dispatch = useDispatch();
  const symbol = selectedAsset?.currency_code?.toUpperCase();

  // Fetch live exchange rate against USD
  const { exchangeRateData, isRateLoading } = useExchangeRate({
    fiatCode: "USD",
    cryptoCode: symbol,
    isSell: true,
    amount,
  });

  // Resolve active exchange rate: prefer API rate, fallback to asset price
  const activeRate = useMemo(() => {
    const liveRate = Number(exchangeRateData?.unit_price ?? exchangeRateData?.unit_price_usd ?? 0);
    return liveRate > 0 ? liveRate : Number(selectedAsset?.unit_price_usd ?? 0);
  }, [exchangeRateData, selectedAsset?.unit_price_usd]);

  const { withdrawalAmount, receiveAmount, fiatApprox } = useMemo(() => {
    const parsedAmount = parseFloat(amount) || 0;
    const receive = Math.max(parsedAmount, 0);

    return {
      withdrawalAmount: parsedAmount,
      receiveAmount: receive,
      fiatApprox: receive * activeRate,
    };
  }, [amount, activeRate]);

  const handleWithdrawSubmit = async () => {
    const today = new Date();
    const transactionDate = [
      String(today.getDate()).padStart(2, "0"),
      String(today.getMonth() + 1).padStart(2, "0"),
      today.getFullYear(),
    ].join("/");

    const formData = new FormData();
    formData.append("account_id", selectedAsset.id);
    formData.append("crypto_beneficiary_id", selectedBeneficiary.id);
    formData.append("transaction_date", transactionDate);
    formData.append("amount", amount);
    formData.append("crypto_amount", amount);

    setSubmitting(true);
    setError("");

    try {
      const response = await dispatch(fireblockCryptoWithdraw(formData));

      if (response?.success) {
        onConfirm();
        return;
      }

      setError(response?.message);
      setCurrentStep(2);
    } catch (err) {
      console.error("Withdraw creation failed:", err);
      setError(err?.message || "Something went wrong while creating your withdraw request. Please try again.");
      setCurrentStep(2);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 px-4 pt-4">
      <h3 className="shrink-0 px-2 text-lg font-semibold leading-7 text-text-primary">
        Confirm Order
      </h3>

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex flex-col items-center gap-1 py-2 text-center">
          <p className="text-sm font-normal leading-[21px] text-text-secondary">
            You&apos;ll receive
          </p>
          <p className="text-[28px] font-semibold leading-8 text-text-primary">
            {receiveAmount} {symbol}
          </p>
          <p className="text-sm font-normal leading-[21px] text-text-secondary">
            {isRateLoading ? (
              <span>Calculating...</span>
            ) : (
              `≈ $${fiatApprox.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            )}
          </p>
        </div>

        <div className="border-t border-divider" />

        <div className="flex flex-col gap-3">
          <SummaryRow label="Assets" value={selectedNetwork?.name} />
          <SummaryRow
            label="Address"
            value={selectedBeneficiary?.wallet_address}
            isAddress
          />
          <SummaryRow
            label="Withdrawal Amount"
            value={`${withdrawalAmount} ${symbol}`}
          />
          <SummaryRow label="Wallet" value={`${symbol} Wallet`} />
        </div>
      </div>

      <div>
        <div className="flex mb-6 items-start gap-2 rounded-[10px] bg-background-alt px-3 py-3">
          <WarningIcon />
          <p className="text-[11.5px] font-normal leading-[18px] text-text-secondary">
            Ensure that address is correct and on the same network. Transactions
            cannot be cancelled
          </p>
        </div>
        <button
          type="button"
          disabled={submitting}
          onClick={handleWithdrawSubmit}
          className="flex h-12 mb-2 w-full shrink-0 items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold leading-[21px] text-white custom-transition hover:bg-[#3fa82c] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Processing..." : "Confirm"}
        </button>
      </div>
    </div>
  );
};

export default WithdrawConfirmOrderStep;
